import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, money, num, fmtDate } from "../lib/api";
import { Button } from "../components/shared";
import { Printer, ArrowLeft } from "lucide-react";
import { CompanyLogo } from "../components/CompanyLogo";

export default function PurchaseOrderPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [po, setPo] = useState(null);
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`/fabric-orders/${id}`).then((r) => setPo(r.data)).catch(() => {}); }, [id]);
  useEffect(() => { api.get("/company").then((r) => setC(r.data || {})).catch(() => setC({})); }, []);
  if (!po || !c) return <div className="grid place-items-center h-96"><div className="h-10 w-10 border-4 border-blue-800 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 no-print">
        <Button variant="secondary" onClick={() => navigate("/purchase-orders")}><ArrowLeft size={15} /> Back</Button>
        <Button onClick={() => window.print()} data-testid="print-po-btn"><Printer size={15} /> Print / Save PDF</Button>
      </div>
      <div id="challan-print" className="bg-white mx-auto shadow-sm border border-slate-200" style={{ maxWidth: "800px", padding: "40px", fontFamily: "Inter, sans-serif" }}>
        <div className="flex items-start justify-between border-b-2 border-slate-800 pb-4">
          <div className="flex items-center gap-3">
            <CompanyLogo hasLogo={c.has_logo} className="h-16 w-16 rounded-lg bg-white border border-slate-200 p-1" fallback={
              <div className="h-14 w-14 rounded-lg bg-blue-800 text-white grid place-items-center text-2xl font-extrabold" style={{ fontFamily: "Outfit" }}>{(c.name || "A").slice(0, 1)}</div>} />
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900 uppercase" style={{ fontFamily: "Outfit" }} data-testid="po-company-name">{c.name || "ASV Apparels"}</h1>
              {c.address && <p className="text-xs text-slate-500 max-w-xs" data-testid="po-company-address">{c.address}</p>}
              <p className="text-xs text-slate-500" data-testid="po-company-ids">{[c.gstin && `GSTIN: ${c.gstin}`, c.pan && `PAN: ${c.pan}`].filter(Boolean).join(" · ")}</p>
              <p className="text-xs text-slate-500">{[c.phone1, c.phone2, c.email, c.website].filter(Boolean).join(" · ")}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-slate-800">PURCHASE ORDER</p>
            <p className="text-sm font-mono font-semibold text-blue-800">{po.code}</p>
            <p className="text-xs text-slate-500 mt-1">Date: {fmtDate(po.order_date)}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-6 mt-5">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Supplier</p>
            <p className="text-sm font-semibold text-slate-800">{po.supplier_name}</p>
            {po.supplier_email && <p className="text-xs text-slate-500">{po.supplier_email}</p>}
            {po.supplier_phone && <p className="text-xs text-slate-500">{po.supplier_phone}</p>}
          </div>
          <div className="text-sm grid grid-cols-2 gap-y-1">
            <span className="text-slate-500">Party Contract</span><span className="font-medium">{po.party_contract || "-"}</span>
            <span className="text-slate-500">Expected Delivery</span><span className="font-medium">{fmtDate(po.expected_receiving_date)}</span>
            <span className="text-slate-500">Status</span><span className="font-medium">{po.status}</span>
          </div>
        </div>
        <table className="w-full mt-5 border border-slate-300 text-sm">
          <thead><tr className="bg-slate-100 text-xs uppercase text-slate-600">
            <th className="border border-slate-300 px-3 py-2 text-left">#</th>
            <th className="border border-slate-300 px-3 py-2 text-left">Item</th>
            <th className="border border-slate-300 px-3 py-2 text-left">Type / Colour</th>
            <th className="border border-slate-300 px-3 py-2 text-right">Qty</th>
            <th className="border border-slate-300 px-3 py-2 text-right">Rate</th>
            <th className="border border-slate-300 px-3 py-2 text-right">Amount</th></tr></thead>
          <tbody>
            {(po.items || []).map((it, i) => (
              <tr key={it.line_id}>
                <td className="border border-slate-300 px-3 py-1.5">{i + 1}</td>
                <td className="border border-slate-300 px-3 py-1.5">{it.fabric_type || it.item_name}</td>
                <td className="border border-slate-300 px-3 py-1.5">{it.category === "Fabric" ? it.colour : it.category}</td>
                <td className="border border-slate-300 px-3 py-1.5 text-right tabular">{num(it.qty)} {it.unit}</td>
                <td className="border border-slate-300 px-3 py-1.5 text-right tabular">{num(it.rate)}</td>
                <td className="border border-slate-300 px-3 py-1.5 text-right tabular">{money(it.amount)}</td>
              </tr>
            ))}
            <tr className="bg-slate-50 font-bold">
              <td className="border border-slate-300 px-3 py-2" colSpan={5}>TOTAL</td>
              <td className="border border-slate-300 px-3 py-2 text-right tabular">{money(po.total_amount)}</td>
            </tr>
          </tbody>
        </table>
        {po.remarks && <p className="text-sm mt-4 text-slate-600"><span className="font-semibold">Remarks:</span> {po.remarks}</p>}
        <div className="grid grid-cols-2 gap-6 mt-16 text-center text-xs text-slate-600">
          {["Prepared By", `For ${c.name || "ASV Apparels"} — Authorised Signatory`].map((s) => <div key={s}><div className="border-t border-slate-400 pt-1">{s}</div></div>)}
        </div>
        <p className="text-center text-[10px] text-slate-400 mt-6">Computer generated purchase order · {c.name || "ASV Apparels"}</p>
      </div>
    </div>
  );
}
